import { z } from "zod";

import { File } from "./file";
import { PromptResponseRatingType as PromptResponseRatingTypeEnum } from "./prompt";
import { AuditorUser } from "./user";

export const MessageCreateInput = z.object({
  threadId: z.string(),
  fileIds: z.array(z.string()),
  promptText: z.string(),
  inputState: z.string(),
  searchKeywords: z.array(z.string()).nullable(),
});
export type MessageCreateInput = z.infer<typeof MessageCreateInput>;

export const MessageByIdInput = z.object({ id: z.number() });
export type MessageByIdInput = z.infer<typeof MessageByIdInput>;

export const MessageByCaseIdInput = z.object({ caseId: z.number() });
export type MessageByCaseIdInput = z.infer<typeof MessageByCaseIdInput>;

export const PromptResponsePage = z.object({
  file: File.pick({ id: true, fileName: true }),
  textMatched: z.string(),
  pageNumber: z.number(),
  confidence: z.number().optional().nullable(),
});
export type PromptResponsePage = z.infer<typeof PromptResponsePage>;

export const PromptResponseRatingType = z.nativeEnum(
  PromptResponseRatingTypeEnum,
);
export type PromptResponseRatingType = z.infer<typeof PromptResponseRatingType>;

export const PromptResponseRating = z.object({
  currentUserRating: PromptResponseRatingType.nullable().optional(),
  downRatings: z.number(),
  upRatings: z.number(),
});
export type PromptResponseRating = z.infer<typeof PromptResponseRating>;

export const PromptResponse = z.object({
  id: z.string(),
  promptText: z.string(),
  responseText: z.string(),
  pages: z.array(PromptResponsePage),
  createdAt: z.string(),
  searchKeywords: z.array(z.string()).nullable().optional(),
  contextFiles: z.array(
    File.pick({ id: true, fileName: true, archived: true }),
  ),
  ratings: PromptResponseRating,
});
export type PromptResponse = z.infer<typeof PromptResponse>;

export const PromptFeedbackLevel = z.enum(["INFO", "WARNING", "ERROR"]);
export type PromptFeedbackLevel = z.infer<typeof PromptFeedbackLevel>;

export const PromptFeedback = z.object({
  level: PromptFeedbackLevel,
  message: z.string(),
});
export type PromptFeedback = z.infer<typeof PromptFeedback>;

export const FileAnnotationCoordinates = z.object({
  height: z.number(),
  left: z.number(),
  top: z.number(),
  width: z.number(),
});
export type FileAnnotationCoordinates = z.infer<
  typeof FileAnnotationCoordinates
>;

export const Annotation = z.object({
  id: z.string(),
  comment: z.string().optional().nullable(),
  createdAt: z.string(),
  labels: z.array(z.string()),
});
export type Annotation = z.infer<typeof Annotation>;

export const FileAiAnnotation = Annotation.extend({
  coordinates: z.array(FileAnnotationCoordinates).optional().nullable(),
  file: File,
  pageIndex: z.number(),
  sourceText: z.string().optional().nullable(),
  updatedAt: z.string(),
});
export type FileAiAnnotation = z.infer<typeof FileAiAnnotation>;

export const FileUserAnnotation = Annotation.extend({
  coordinates: z.array(FileAnnotationCoordinates).optional().nullable(),
  creator: AuditorUser,
  file: File,
  pageIndex: z.number(),
  sourceText: z.string().optional().nullable(),
  updatedAt: z.string(),
});
export type FileUserAnnotation = z.infer<typeof FileUserAnnotation>;

export const InlineCitationType = z.enum(["FILE_PREVIEW"]);
export type InlineCitationType = z.infer<typeof InlineCitationType>;

export const InlineCitation = z.object({
  annotation: FileAiAnnotation.or(FileUserAnnotation),
  citationType: InlineCitationType,
  key: z.string(),
});
export type InlineCitation = z.infer<typeof InlineCitation>;

export const Message = z.object({
  id: z.string(),
  citations: z.array(InlineCitation).optional().nullable(),
  promptText: z.string(),
  promptInputState: z.string(),
  response: PromptResponse.optional().nullable(),
  createdAt: z.string(),
  feedback: z.array(PromptFeedback).optional().nullable(),
});
export type Message = z.infer<typeof Message>;

export const Thread = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  messages: z.array(Message),
});
export type Thread = z.infer<typeof Thread>;
